import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Mail, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col">
      <header className="p-4">
        <Link to="/login">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sign In
          </Button>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 pb-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="MISYOU" className="w-14 h-14 rounded-xl object-cover mx-auto mb-3" />
            <h1 className="text-3xl font-black text-slate-900">MISYOU</h1>
          </div>

          <Card className="shadow-xl" data-testid="forgot-password-page">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-2xl">Forgot your password?</CardTitle>
              <CardDescription>
                Enter the email you signed up with. We&rsquo;ll send you a secure reset
                link that lets you choose a new password within the next 60 minutes.
              </CardDescription>
            </CardHeader>

            <CardContent>
              {done ? (
                <Alert className="bg-emerald-50 border-emerald-200" data-testid="forgot-password-success">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  <AlertDescription className="text-emerald-800">
                    <p className="font-medium mb-1">Check your inbox.</p>
                    <p className="text-sm">
                      If an account exists for that email, we&rsquo;ve sent a password-reset
                      link from <strong>contact@misyou.in</strong>. Click the button in the
                      email to choose a new password — the link expires in <strong>60
                      minutes</strong>.
                    </p>
                    <p className="text-xs mt-2">
                      Didn&rsquo;t get it? Check your spam folder, or email
                      {' '}<a href="mailto:contact@misyou.in" className="underline">contact@misyou.in</a> and our team can help manually.
                    </p>
                  </AlertDescription>
                </Alert>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" data-testid="forgot-password-error">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12"
                        required
                        autoComplete="email"
                        data-testid="forgot-password-email"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-primary hover:bg-primary/90 rounded-xl text-base btn-press"
                    disabled={submitting}
                    data-testid="forgot-password-submit"
                  >
                    {submitting ? 'Submitting…' : 'Request password reset'}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Remembered it? <Link to="/login" className="text-primary underline">Sign in</Link> instead.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
